import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useXP } from './useXP';
import { usePointsTransactions } from './usePointsTransactions';
import { celebrateMilestone } from '@/utils/confetti';

const XP_PER_VIDEO = 10;
const POINTS_PER_VIDEO = 5;
const MAX_VIDEOS_PER_DAY = 5;

export const useRewardedVideo = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { addXP } = useXP();
  const { earnPoints } = usePointsTransactions();
  const [isWatching, setIsWatching] = useState(false);
  const [videosWatchedToday, setVideosWatchedToday] = useState(0);

  const checkDailyLimit = async (): Promise<boolean> => {
    if (!user) return false;

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { count, error } = await supabase
        .from('rewarded_video_views')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .gte('created_at', today.toISOString());

      if (error) throw error;

      setVideosWatchedToday(count || 0);
      return (count || 0) < MAX_VIDEOS_PER_DAY;
    } catch (error) {
      console.error('Error checking daily limit:', error);
      return false;
    }
  };

  const watchRewardedVideo = async (adId?: string): Promise<boolean> => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to watch videos and earn rewards",
        variant: "destructive"
      });
      return false;
    }

    // Check daily limit
    const canWatch = await checkDailyLimit();
    if (!canWatch) {
      toast({
        title: "Daily limit reached",
        description: `You've watched ${MAX_VIDEOS_PER_DAY} videos today. Come back tomorrow!`,
        variant: "destructive"
      });
      return false;
    }

    setIsWatching(true);

    try {
      // Simulate video watch (in production, this would integrate with AdMob/Unity Ads)
      // For now, we'll just wait 5 seconds
      await new Promise(resolve => setTimeout(resolve, 5000));

      // Log the view
      const { error: logError } = await supabase
        .from('rewarded_video_views')
        .insert({
          user_id: user.id,
          ad_id: adId || null,
          xp_earned: XP_PER_VIDEO,
          points_earned: POINTS_PER_VIDEO,
          completed: true
        });

      if (logError) throw logError;

      // Award rewards
      await addXP(XP_PER_VIDEO);
      await earnPoints(POINTS_PER_VIDEO, 'rewarded_video');

      // Celebration
      celebrateMilestone();

      toast({
        title: "Rewards Earned! 🎉",
        description: `+${XP_PER_VIDEO} XP, +${POINTS_PER_VIDEO} points`,
      });

      // Update counter
      setVideosWatchedToday(prev => prev + 1);

      return true;
    } catch (error) {
      console.error('Error watching video:', error);
      toast({
        title: "Error",
        description: "Failed to complete video. Please try again.",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsWatching(false);
    }
  };

  return {
    watchRewardedVideo,
    isWatching,
    videosWatchedToday,
    maxVideosPerDay: MAX_VIDEOS_PER_DAY,
    videosRemaining: MAX_VIDEOS_PER_DAY - videosWatchedToday,
    checkDailyLimit
  };
};
